import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import AuthContext from 'context/AuthContext';
import dayjs from 'dayjs';
import useToastContext from 'hooks/useToastContext';
import { isEmpty } from 'lodash';
import Pagination from 'pages/Pagination';
import { useCallback, useContext, useEffect, useState } from 'react';
import { TiEye, TiTrash } from 'react-icons/ti';
import { useNavigate } from 'react-router-dom';
import { apiDelete, apiGet, apiPut } from 'utils/apiFetch';
import apiPath from 'utils/apiPath';
import helpers from 'utils/helpers';
import { formatTime12h } from 'utils/timeFormat';

const pageSize = 10;

function getSessionOptionLabel(session) {
  if (!session) return '';
  const dateLabel = session.date
    ? dayjs(session.date).format('DD-MM-YYYY')
    : 'N/A';
  return `${session.location || 'N/A'} · ${dateLabel}`;
}

export default function TrainingSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [attendedFilter, setAttendedFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [keyword, setKeyword] = useState('');
  const { user } = useContext(AuthContext);
  const notification = useToastContext();
  const navigate = useNavigate();
  const permissions = user?.permission?.find(
    (item) => item.manager === 'training_session_manager',
  );
  const canEdit = permissions?.add || user?.role === 'admin';
  const canDelete = permissions?.delete || user?.role === 'admin';

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setKeyword(search.trim());
    }, 300);

    return () => clearTimeout(timeout);
  }, [search]);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await apiGet(apiPath.trainingSessions, {
        page: 1,
        pageSize: 500,
      });
      setSessions(response?.data?.results?.docs || []);
    } catch (error) {
      notification.error(
        error?.response?.data?.message || 'Unable to load training sessions.',
      );
    }
  }, [notification]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await apiGet(apiPath.trainingSubmissions, {
        page,
        pageSize,
        ...(keyword && { keyword }),
        ...(selectedSessionIds.length > 0 && {
          trainingSessionId: selectedSessionIds,
        }),
        ...(startDate && {
          start_date: dayjs(startDate).format('YYYY-MM-DD'),
        }),
        ...(endDate && {
          end_date: dayjs(endDate).format('YYYY-MM-DD'),
        }),
        ...(attendedFilter && { isAttended: attendedFilter }),
      });
      setSubmissions(response?.data?.results?.docs || []);
      setTotalPages(response?.data?.results?.totalPages || 0);
      setTotalItems(response?.data?.results?.totalDocs || 0);
    } catch (error) {
      notification.error(
        error?.response?.data?.message ||
          'Unable to load training submissions.',
      );
    }
  }, [
    attendedFilter,
    endDate,
    keyword,
    notification,
    page,
    selectedSessionIds,
    startDate,
  ]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  async function changeAttendance(submission) {
    try {
      const isAttended = !submission.isAttended;
      const response = await apiPut(
        `${apiPath.trainingSubmissions}/${submission._id}`,
        { isAttended },
      );
      if (!response?.data?.success) return;

      notification.success(
        response?.data?.message || 'Attendance updated successfully.',
      );
      fetchSubmissions();
    } catch (error) {
      notification.error(
        error?.response?.data?.message || 'Unable to update attendance.',
      );
    }
  }

  async function deleteSubmission(submissionId) {
    try {
      const response = await apiDelete(
        `${apiPath.trainingSubmissions}/${submissionId}`,
      );
      if (!response?.data?.success) return;

      notification.success('Training submission deleted successfully.');
      if (submissions.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        fetchSubmissions();
      }
    } catch (error) {
      notification.error(
        error?.response?.data?.message || 'Unable to delete submission.',
      );
    }
  }

  return (
    <div className='bg-white px-3 py-4'>
      <h1 className='text-2xl font-bold'>Training Submissions</h1>
      <hr className='my-4' />

      <div className='flex flex-wrap items-start gap-3'>
        <TextField
          label='Search'
          variant='outlined'
          size='small'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Agency, contact, email or mobile'
          className='min-w-[240px]'
        />

        <FormControl className='min-w-[280px] max-w-[360px] flex-1'>
          <Autocomplete
            multiple
            size='small'
            options={sessions}
            getOptionLabel={getSessionOptionLabel}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={sessions.filter((session) =>
              selectedSessionIds.includes(session._id),
            )}
            onChange={(_, newValue) => {
              setPage(1);
              setSelectedSessionIds(newValue.map((session) => session._id));
            }}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                label='Training Session'
                placeholder='Select sessions'
              />
            )}
          />
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label='Start Date'
            inputFormat='DD-MM-YYYY'
            value={startDate}
            onChange={(value) => {
              setPage(1);
              setStartDate(value);
            }}
            renderInput={(params) => (
              <TextField {...params} size='small' className='w-[160px]' />
            )}
          />
          <DesktopDatePicker
            label='End Date'
            inputFormat='DD-MM-YYYY'
            value={endDate}
            minDate={startDate || undefined}
            onChange={(value) => {
              setPage(1);
              setEndDate(value);
            }}
            renderInput={(params) => (
              <TextField {...params} size='small' className='w-[160px]' />
            )}
          />
        </LocalizationProvider>

        <FormControl size='small' className='max-w-[160px] w-full'>
          <InputLabel id='attended-filter-label'>Attended</InputLabel>
          <Select
            labelId='attended-filter-label'
            label='Attended'
            value={attendedFilter}
            onChange={(e) => {
              setPage(1);
              setAttendedFilter(e.target.value);
            }}>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='true'>Attended</MenuItem>
            <MenuItem value='false'>Not Attended</MenuItem>
          </Select>
        </FormControl>
      </div>

      <hr className='my-4' />
      <div className='overflow-x-auto border'>
        <table className='w-full text-left text-xs text-[#A5A5A5]'>
          <thead className='border border-[#E1E6EE] bg-[#E1E6EE] text-gray-900'>
            <tr>
              <th className='px-4 py-3'>#</th>
              <th className='px-4 py-3'>Training Session</th>
              <th className='px-4 py-3'>Attendees</th>
              <th className='px-4 py-3'>Agency</th>
              <th className='px-4 py-3'>Contact Person</th>
              <th className='px-4 py-3'>Mobile</th>
              <th className='px-4 py-3'>Email</th>
              <th className='px-4 py-3'>Created At</th>
              <th className='px-4 py-3 text-center'>Attended</th>
              <th className='px-4 py-3 text-center'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((submission, index) => (
              <tr className='border-b border-[#E1E6EE]' key={submission._id}>
                <td className='border-l px-4 py-3'>
                  {(page - 1) * pageSize + index + 1}
                </td>
                <td className='border-l px-4 py-3'>
                  <div className='whitespace-nowrap font-medium text-gray-700'>
                    {submission.trainingSessionId?.location || 'N/A'}
                  </div>
                  <div className='whitespace-nowrap'>
                    {submission.trainingSessionId?.date
                      ? dayjs(submission.trainingSessionId.date).format(
                          'DD-MM-YYYY',
                        )
                      : 'N/A'}
                    {' · '}
                    {formatTime12h(
                      submission.trainingSessionId?.startTime,
                    )} - {formatTime12h(submission.trainingSessionId?.endTime)}
                  </div>
                </td>
                <td className='border-l px-4 py-3'>
                  {submission.numberOfAttendees}
                </td>
                <td className='border-l px-4 py-3'>{submission.agencyName}</td>
                <td className='border-l px-4 py-3'>
                  {submission.contactPerson}
                </td>
                <td className='border-l px-4 py-3'>
                  {submission.mobileNumber}
                </td>
                <td className='border-l px-4 py-3'>{submission.email}</td>
                <td className='whitespace-nowrap border-l px-4 py-3'>
                  {dayjs(submission.createdAt).format('DD-MM-YYYY hh:mm A')}
                </td>
                <td className='border-l px-4 py-3 text-center'>
                  <Switch
                    checked={!!submission.isAttended}
                    color='success'
                    disabled={!canEdit}
                    onChange={(e) => {
                      if (!canEdit) return;
                      helpers.alertFunction(
                        `Are you sure you want to mark this submission as ${
                          e.target.checked ? 'attended' : 'not attended'
                        }?`,
                        submission,
                        changeAttendance,
                      );
                    }}
                    inputProps={{ 'aria-label': 'Toggle attendance' }}
                  />
                </td>
                <td className='border-l px-4 py-3'>
                  <div className='flex justify-center gap-2'>
                    <button
                      type='button'
                      title='View submission'
                      className='rounded-full bg-blue-500 p-2 text-white hover:bg-blue-700 transition-colors duration-150'
                      onClick={() =>
                        navigate(
                          `/training-sessions/submissions/details/${submission._id}`,
                        )
                      }>
                      <TiEye />
                    </button>
                    {canDelete && (
                      <button
                        type='button'
                        title='Delete submission'
                        className='rounded-full bg-red-500 p-2 text-white hover:bg-red-700 transition-colors duration-150'
                        onClick={() =>
                          helpers.alertFunction(
                            `Are you sure want to delete ${
                              submission.agencyName || 'this submission'
                            }?`,
                            submission._id,
                            deleteSubmission,
                            true,
                          )
                        }>
                        <TiTrash />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {isEmpty(submissions) && (
              <tr className='border-b bg-white text-center'>
                <td className='px-6 py-4' colSpan={10}>
                  No training submissions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalItems > 0 && (
          <Pagination
            handlePageClick={({ selected }) => setPage(selected + 1)}
            options={{
              totalItems,
              pageCount: totalPages,
              pageRangeDisplayed: 5,
            }}
            page={page}
          />
        )}
      </div>
    </div>
  );
}
